import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../../config/api';
import { useTheme } from '../../theme/ThemeContext';
import { ModalBackdrop } from '../common/ModalBackdrop';
import { getProfilePictureUrl } from '../../utils/profilePicture';
import { clearPatientsCache } from '../../utils/referenceDataCache';
import { formatDate } from '../../utils/dateFormat';
import { BolivianDateInput, BolivianTimeInput } from '../common/BolivianDateInput';
import type {
  Appointment,
  AppointmentStatus,
  CalendarAppointmentDetail,
  CalendarDetailCharge,
  CalendarDetailTreatment,
  Doctor,
  Laboratory,
  TreatmentTemplate
} from './types';
import type { AppointmentFormState } from './AppointmentModal';
import { DEFAULT_APPOINTMENT_REASON_VALUE, isDefaultAppointmentReasonName } from './appointmentReason';
import './AppointmentModal.css';

interface Props {
  open: boolean;
  token: string;
  appointment: Appointment;
  form: AppointmentFormState;
  setForm: (next: AppointmentFormState) => void;
  doctors: Doctor[];
  laboratories?: Laboratory[];
  treatmentTemplates: TreatmentTemplate[];
  onClose: () => void;
  onDelete: () => void;
  onSaveDraft?: () => Promise<void>;
  onOpenPatientProfile?: (patientId: number) => void;
  onOpenTreatmentWorkspace?: (treatmentId: number, patientId?: number | null) => void;
  onRefreshAppointment?: () => Promise<void>;
  onStatusChanged?: (appointmentId: number, status: AppointmentStatus) => void;
  canEditAppointments?: boolean;
  canEditPayments?: boolean;
  canViewPayments?: boolean;
  onReservationFeePayment?: (data: { amount: number; date: string; method: string; notes?: string }) => Promise<void>;
  onReservationFeeUpdate?: (amount: number) => Promise<void>;
  onReservationFeeRemove?: () => Promise<void>;
  onReservationFeeRefund?: (data: { amount: number; date: string; method: string; notes?: string }) => Promise<void>;
}

type OperationalTab = 'appointment' | 'patient' | 'payment';

interface PatientCheckoutDraft {
  hc: string;
  firstName: string;
  secondName: string;
  firstLastName: string;
  secondLastName: string;
  presentedName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: string;
  address: string;
  referralSource: string;
  nextContact: string;
  notes: string;
  isForeignPatient: boolean;
}

interface TreatmentFinanceDraft {
  price: string;
  discountEnabled: boolean;
  discountMode: 'amount' | 'percent';
  discountValue: string;
  invoice: boolean;
  specialMaterialsCost: string;
  laboratoryId: string;
  laboratoryCost: string;
  mechanicName: string;
}

interface ChargeEditDraft {
  concept: string;
  amount: string;
}

const emptyPatientDraft: PatientCheckoutDraft = {
  hc: '',
  firstName: '',
  secondName: '',
  firstLastName: '',
  secondLastName: '',
  presentedName: '',
  phoneNumber: '',
  email: '',
  dateOfBirth: '',
  address: '',
  referralSource: '',
  nextContact: '',
  notes: '',
  isForeignPatient: false
};

const patientDraftFromDetail = (patient: CalendarAppointmentDetail['patient'], appointment?: Appointment): PatientCheckoutDraft => {
  const prospectName = appointment?.prospectName?.trim() || '';
  const patientName = patient?.name || prospectName;
  const parts = patientName.trim().split(/\s+/).filter(Boolean);
  const importedNotes = [appointment?.prospectNotes, appointment?.notes]
    .filter((value): value is string => !!value?.trim())
    .join('\n');
  return {
    hc: patient?.hc || '',
    firstName: patient?.firstName || parts[0] || '',
    secondName: patient?.secondName || '',
    firstLastName: patient?.firstLastName || parts.slice(1).join(' ') || parts[0] || '',
    secondLastName: patient?.secondLastName || '',
    presentedName: patient?.presentedName || patient?.name || prospectName,
    phoneNumber: patient?.phoneNumber || appointment?.prospectContact || '',
    email: patient?.email || '',
    dateOfBirth: patient?.dateOfBirth?.slice(0, 10) || '',
    address: patient?.address || '',
    referralSource: patient?.referralSource || '',
    nextContact: patient?.nextContact?.slice(0, 10) || '',
    notes: patient?.notes || importedNotes,
    isForeignPatient: !!patient?.isForeignPatient
  };
};

const patientNeedsCompletion = (
  patient: CalendarAppointmentDetail['patient'],
  linkedPatientId?: number | null,
  prospectName?: string | null
) =>
  (!!prospectName?.trim() && !(patient?.id || linkedPatientId)) ||
  (!!(patient?.id || linkedPatientId) &&
    (!patient || !patient.hc || patient.status === 'New' || patient.status === 0 || patient.statusName === 'New'));

const treatmentFinanceDraftFromDetail = (treatment: CalendarDetailTreatment): TreatmentFinanceDraft => {
  // The link stores an absolute discount on this appointment's price snapshot.
  const discountValue = Number(treatment.discount ?? treatment.discountAmount ?? 0);
  return {
    price: String(Number(treatment.price || 0)),
    discountEnabled: discountValue > 0.005,
    discountMode: 'amount',
    discountValue: discountValue > 0.005 ? String(discountValue) : '',
    invoice: !!treatment.invoice,
    specialMaterialsCost: String(Number(treatment.specialMaterialsCost || 0)),
    laboratoryId: treatment.laboratoryId == null ? '' : String(treatment.laboratoryId),
    laboratoryCost: String(Number(treatment.laboratoryCost || 0)),
    mechanicName: treatment.mechanicName || ''
  };
};

const treatmentFinanceDraftsFromDetail = (treatments: CalendarDetailTreatment[]) =>
  Object.fromEntries(treatments.map(treatment => [treatment.treatmentId, treatmentFinanceDraftFromDetail(treatment)]));

const treatmentNetPrice = (treatment: CalendarDetailTreatment) => {
  const gross = Number(treatment.price || 0) * Number(treatment.quantity || 1);
  const discount = Number(treatment.discount ?? treatment.discountAmount ?? 0);
  return Math.max(0, gross - discount);
};

const money = (value?: number | null) => `Bs ${Number(value || 0).toFixed(2)}`;
const paymentMethods = ['Cash', 'QR', 'Card', 'Transfer', 'Other'] as const;

const fallbackDetail = (appointment: Appointment): CalendarAppointmentDetail => {
  const linkedTreatments = appointment.treatments?.length
    ? appointment.treatments
    : appointment.treatment
      ? [appointment.treatment]
      : [];
  const paid = (appointment.payments || []).reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const currentAppointment = Math.max(0, Number(appointment.chargeAmount || 0) - paid);
  return {
    appointment: {
      id: appointment.id,
      patientId: appointment.patientId,
      doctorId: appointment.doctorId,
      secondDoctorId: appointment.secondDoctorId,
      room: appointment.room,
      startTime: appointment.startTime,
      endTime: appointment.endTime,
      duration: appointment.duration,
      notes: appointment.notes,
      treatedTeeth: appointment.treatedTeeth,
      status: appointment.status,
      isNoShow: appointment.isNoShow,
      canceledAt: appointment.canceledAt,
      statusLabel: appointment.isNoShow ? 'No-show' : appointment.status || 'scheduled',
      appointmentType: linkedTreatments.map(item => item.name).join(', ') || appointment.treatmentTemplate?.name || appointment.customReason || 'Appointment'
    },
    patient: appointment.patient ? {
      id: appointment.patientId || 0,
      name: appointment.patient.name,
      hc: appointment.patient.hc,
      email: appointment.patient.email,
      status: appointment.patient.status,
      profilePicturePath: appointment.patient.profilePicturePath,
      hasCriticalAllergy: false,
      allergies: []
    } : null,
    doctor: appointment.doctor ? { id: appointment.doctorId || 0, name: `${appointment.doctor.firstName} ${appointment.doctor.lastName}`.trim() } : null,
    secondDoctor: null,
    financial: {
      previousDebt: Number(appointment.financialSummary?.pastDue || 0),
      currentAppointment,
      totalDue: Number(appointment.financialSummary?.patientBalanceDue || currentAppointment),
      charges: []
    },
    treatments: linkedTreatments.map(item => ({
      treatmentId: item.id,
      treatmentName: item.name,
      executionStatus: 'planned',
      status: 'planned',
      quantity: 1,
      price: Number(item.cost || 0),
      discount: 0,
      isIncludedFree: false,
      doctorId: item.doctorId ?? null,
      paid: 0,
      due: Number(item.cost || 0),
      notes: ''
    })),
    payments: [],
    ledger: [],
    history: [],
    reservationFee: appointment.reservationFee ?? null
  };
};

export const AppointmentOperationalModal: React.FC<Props> = ({
  open,
  token,
  appointment,
  form,
  setForm,
  doctors,
  laboratories = [],
  treatmentTemplates,
  onClose,
  onDelete,
  onSaveDraft,
  onOpenPatientProfile,
  onOpenTreatmentWorkspace,
  onRefreshAppointment,
  onStatusChanged,
  canEditAppointments = false,
  canEditPayments = false,
  canViewPayments = false,
  onReservationFeePayment,
  onReservationFeeUpdate,
  onReservationFeeRemove
}) => {
  const { tokens } = useTheme();
  const { t, i18n } = useTranslation();
  const tt = useCallback((key: string, fallback: string) => t(`calendar.operational.${key}`, fallback), [t]);
  const paymentMethodOptions = paymentMethods.map(method => ({
    value: method,
    label: tt(`paymentMethods.${method.toLowerCase()}`, method)
  }));
  const [detail, setDetail] = useState<CalendarAppointmentDetail>(() => fallbackDetail(appointment));
  const [activeTab, setActiveTab] = useState<OperationalTab>('appointment');
  const [patientDraft, setPatientDraft] = useState<PatientCheckoutDraft>(emptyPatientDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [reservationOpen, setReservationOpen] = useState(false);
  const [reservationFeeEditorOpen, setReservationFeeEditorOpen] = useState(false);
  const [reservationFeeAmount, setReservationFeeAmount] = useState('50');
  const [payment, setPayment] = useState({ amount: '', method: 'Cash', notes: '' });
  const [reservationPayment, setReservationPayment] = useState({ amount: '', method: 'Cash', notes: '' });
  const [treatmentFinanceDrafts, setTreatmentFinanceDrafts] = useState<Record<number, TreatmentFinanceDraft>>({});
  const [advancedTreatmentIds, setAdvancedTreatmentIds] = useState<Set<number>>(() => new Set());
  const [editingChargeId, setEditingChargeId] = useState<string | null>(null);
  const [chargeEditDraft, setChargeEditDraft] = useState<ChargeEditDraft>({ concept: '', amount: '' });

  const loadDetail = useCallback(async () => {
    if (!token) {
      setDetail(fallbackDetail(appointment));
      return;
    }
    try {
      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/calendar-detail`), {
        headers: getAuthHeaders(token)
      });
      if (!response.ok) throw new Error(await response.text());
      const loaded = await response.json();
      setDetail(loaded);
      setPatientDraft(patientDraftFromDetail(loaded.patient, appointment));
      setTreatmentFinanceDrafts(treatmentFinanceDraftsFromDetail(loaded.treatments || []));
      setActiveTab(current => current === 'patient' && !patientNeedsCompletion(loaded.patient, loaded.appointment?.patientId || appointment.patientId, appointment.prospectName) ? 'payment' : current);
    } catch {
      setDetail(fallbackDetail(appointment));
    }
  }, [appointment, token]);

  useEffect(() => {
    if (!open) return;
    setDetail(fallbackDetail(appointment));
    setError(null);
    setNotice(null);
    setPaymentOpen(false);
    setReservationOpen(false);
    setReservationFeeEditorOpen(false);
    setReservationFeeAmount(String(appointment.reservationFee?.amount ?? 50));
    setAdvancedTreatmentIds(new Set());
    setEditingChargeId(null);
    const fallback = fallbackDetail(appointment);
    setPatientDraft(patientDraftFromDetail(fallback.patient, appointment));
    setTreatmentFinanceDrafts(treatmentFinanceDraftsFromDetail(fallback.treatments));
    setActiveTab(patientNeedsCompletion(fallback.patient, fallback.appointment.patientId || appointment.patientId, appointment.prospectName) ? 'patient' : 'appointment');
    void loadDetail();
  }, [appointment, loadDetail, open]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      await onRefreshAppointment?.();
      await loadDetail();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : tt('errors.actionFailed', 'Action failed'));
    } finally {
      setBusy(false);
    }
  };

  const isNoShow = !!detail.appointment.isNoShow || !!appointment.isNoShow;
  const appointmentStatus = String(detail.appointment.status || appointment.status || 'scheduled')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  const isConfirmed = appointmentStatus === 'confirmed';
  const reservationFee = detail.reservationFee ?? appointment.reservationFee ?? null;
  const confirmationRequiresReservation = !isConfirmed
    && !!reservationFee
    && Number(reservationFee.amount || 0) > 0.005
    && Number(reservationFee.balance || 0) > 0.005;
  const canToggleConfirmation = canEditAppointments
    && !isNoShow
    && !appointment.isAllDay
    && !!(detail.appointment.startTime || appointment.startTime)
    && !detail.appointment.canceledAt
    && !appointment.canceledAt
    && (appointmentStatus === 'scheduled' || isConfirmed);
  const currentDue = Number(detail.financial.currentAppointment || 0);
  const balanceColor = tokens.isLight ? '#b91c1c' : '#fca5a5';
  const needsPatientCompletion = patientNeedsCompletion(detail.patient, detail.appointment.patientId || appointment.patientId, appointment.prospectName);
  const canComplete = canEditAppointments && appointmentStatus === 'ready_for_checkout';
  const treatmentPrice = detail.treatments.reduce((sum, treatment) => sum + treatmentNetPrice(treatment), 0);
  const reservationCreditApplied = detail.treatments.reduce(
    (sum, treatment) => sum + Number(treatment.reservationCreditApplied || 0),
    0
  );
  const reservationPendingOffset = detail.treatments.reduce(
    (sum, treatment) => sum + Number(treatment.reservationPendingOffset || 0),
    0
  );
  const reservationAppliedToAppointment = reservationCreditApplied + reservationPendingOffset;
  const plannedReservationPayment = reservationOpen ? Number(reservationPayment.amount || 0) : 0;
  const checkoutAppointmentBalance = currentDue;
  const checkoutReservationBalance = Math.max(
    0,
    Number(reservationFee?.balance || 0) - (Number.isFinite(plannedReservationPayment) ? plannedReservationPayment : 0)
  );
  const checkoutCombinedBalance = checkoutAppointmentBalance + checkoutReservationBalance;
  const linkedTreatment = detail.treatments[0];
  const patientId = detail.patient?.id || appointment.patientId || null;
  const previousDebtCharges = useMemo(() => detail.financial.charges
    .filter(charge => !charge.isCurrentAppointment && Number(charge.pending || 0) > 0.005)
    .sort((left, right) => new Date(left.dueDate).getTime() - new Date(right.dueDate).getTime()), [detail.financial.charges]);
  const currentAccountCharges = useMemo(() => detail.financial.charges
    .filter(charge => charge.isCurrentAppointment && charge.accountEntryId && Number(charge.pending || 0) > 0.005), [detail.financial.charges]);
  const activeTreatmentCharges = detail.treatments.filter(treatment => !treatment.isIncludedFree);
  const appointmentPayments = useMemo(() => {
    const grouped = new Map<string, {
      id: number;
      date: string;
      amount: number;
      method: string;
      notes?: string | null;
      appliedTo: string[];
    }>();

    detail.payments
      .filter(item => item.appointmentId === appointment.id && !item.patientReservationFeeId)
      .forEach(item => {
        const key = item.paymentGroupId || `payment:${item.id}`;
        const current = grouped.get(key);
        if (current) {
          current.amount += Number(item.amount || 0);
          if (item.appliedTo && !current.appliedTo.includes(item.appliedTo)) current.appliedTo.push(item.appliedTo);
          return;
        }
        grouped.set(key, {
          id: item.id,
          date: item.date,
          amount: Number(item.amount || 0),
          method: item.method,
          notes: item.notes,
          appliedTo: item.appliedTo ? [item.appliedTo] : []
        });
      });

    return Array.from(grouped.values());
  }, [appointment.id, detail.payments]);

  const selectedTreatmentIds = form.treatmentIds?.length
    ? form.treatmentIds
    : form.treatmentId
      ? [form.treatmentId]
      : [];
  const legacyTemplate = treatmentTemplates.find(template =>
    String(template.id) === form.treatmentTemplateId ||
    (form.treatmentTemplateId === DEFAULT_APPOINTMENT_REASON_VALUE &&
      (isDefaultAppointmentReasonName(template.name) || isDefaultAppointmentReasonName(template.nameEs))));
  const selectedTemplateSelections = form.treatmentTemplateSelections?.length
    ? form.treatmentTemplateSelections
    : legacyTemplate
      ? [{
          treatmentTemplateId: String(legacyTemplate.id),
          price: String(Number(legacyTemplate.suggestedPrice ?? legacyTemplate.suggestedCost ?? 0))
        }]
      : [];
  const hasSelectedTreatments = selectedTreatmentIds.length > 0 || selectedTemplateSelections.length > 0;
  const consultationTemplate = treatmentTemplates.find(template =>
    template.name?.trim().toLocaleLowerCase() === 'consulta') ?? treatmentTemplates.find(template =>
    template.nameEs?.trim().toLocaleLowerCase() === 'consulta') ?? treatmentTemplates.find(template =>
    isDefaultAppointmentReasonName(template.name) || isDefaultAppointmentReasonName(template.nameEs));

  const addTreatment = (value: string) => {
    if (value.startsWith('treatment:')) {
      const id = value.slice('treatment:'.length);
      if (selectedTreatmentIds.includes(id)) return;
      const nextIds = [...selectedTreatmentIds, id];
      setForm({ ...form, treatmentId: nextIds[0], treatmentIds: nextIds, treatmentTemplateId: '', customReason: '' });
      return;
    }
    if (value.startsWith('template:')) {
      const id = value.slice('template:'.length);
      if (selectedTemplateSelections.some(selection => selection.treatmentTemplateId === id)) return;
      const template = treatmentTemplates.find(item => String(item.id) === id);
      if (!template) return;
      setForm({
        ...form,
        treatmentId: selectedTreatmentIds[0] || '',
        treatmentIds: selectedTreatmentIds,
        treatmentTemplateId: '',
        treatmentTemplateSelections: [
          ...selectedTemplateSelections,
          { treatmentTemplateId: id, price: String(Number(template.suggestedPrice ?? template.suggestedCost ?? 0)) }
        ],
        customReason: ''
      });
      return;
    }
    if (value === 'consultation') {
      if (consultationTemplate) {
        addTreatment(`template:${consultationTemplate.id}`);
        return;
      }
      setForm({
        ...form,
        treatmentId: '',
        treatmentIds: [],
        treatmentTemplateId: DEFAULT_APPOINTMENT_REASON_VALUE,
        treatmentTemplateSelections: [],
        customReason: t('calendar.modal.defaultConsultationReason', 'Consultation')
      });
      return;
    }
    if (value === 'other') {
      setForm({ ...form, treatmentId: '', treatmentIds: [], treatmentTemplateId: '__other__', treatmentTemplateSelections: [], customReason: form.customReason });
    }
  };

  const removeTreatment = (kind: 'treatment' | 'template', id: string) => {
    if (kind === 'treatment') {
      const nextIds = selectedTreatmentIds.filter(item => item !== id);
      setForm({ ...form, treatmentId: nextIds[0] || '', treatmentIds: nextIds });
      return;
    }
    setForm({
      ...form,
      treatmentTemplateId: '',
      treatmentTemplateSelections: selectedTemplateSelections.filter(selection => selection.treatmentTemplateId !== id)
    });
  };

  const displayTemplateName = (template: TreatmentTemplate) =>
    i18n?.language?.startsWith('es') ? template.nameEs || template.name : template.name;

  const saveAppointment = () => run(async () => {
    if (!onSaveDraft) throw new Error(tt('errors.saveUnavailable', 'Save action unavailable'));
    await onSaveDraft();
  });

  const markNoShow = () => run(async () => {
    const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/no-show`), {
      method: 'PUT',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ isNoShow: !isNoShow })
    });
    if (!response.ok) throw new Error(await response.text() || tt('errors.noShowFailed', 'Failed to mark no-show'));
    setForm({ ...form, isNoShow: !isNoShow });
  });

  const toggleConfirmation = () => run(async () => {
    if (confirmationRequiresReservation) {
      throw new Error(tt('confirmation.requiresReservation', 'Pay the reservation fee in full before confirming this appointment.'));
    }
    const response = await fetch(
      isConfirmed
        ? getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/lifecycle/undo?targetStatus=scheduled`)
        : getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/lifecycle`),
      {
        method: isConfirmed ? 'POST' : 'PUT',
        headers: getAuthHeaders(token),
        body: isConfirmed ? undefined : JSON.stringify({ targetStatus: 'confirmed' })
      }
    );
    if (!response.ok) throw new Error(await response.text() || tt('errors.confirmationFailed', 'Could not update appointment confirmation.'));
    const updated = await response.json();
    const nextStatus = String(updated.status || (isConfirmed ? 'scheduled' : 'confirmed')) as AppointmentStatus;
    setDetail(current => ({
      ...current,
      appointment: {
        ...current.appointment,
        status: nextStatus,
        statusLabel: t(`calendar.lifecycle.status.${nextStatus}`, nextStatus)
      }
    }));
    setForm({ ...form, status: nextStatus });
    onStatusChanged?.(appointment.id, nextStatus);
  });

  const updateTreatmentFinanceDraft = (treatmentId: number, changes: Partial<TreatmentFinanceDraft>) => {
    setTreatmentFinanceDrafts(current => ({
      ...current,
      [treatmentId]: { ...current[treatmentId], ...changes }
    }));
  };

  const submitTreatmentFinance = (event: React.FormEvent, treatment: CalendarDetailTreatment) => {
    event.preventDefault();
    const draft = treatmentFinanceDrafts[treatment.treatmentId];
    if (!draft) return;
    void run(async () => {
      const price = Number(draft.price);
      const discountValue = draft.discountEnabled ? Number(draft.discountValue || 0) : 0;
      const specialMaterialsCost = Number(draft.specialMaterialsCost || 0);
      const laboratoryCost = Number(draft.laboratoryCost || 0);
      if (![price, discountValue, specialMaterialsCost, laboratoryCost].every(Number.isFinite) ||
          price < 0 || discountValue < 0 || specialMaterialsCost < 0 || laboratoryCost < 0) {
        throw new Error(tt('errors.invalidTreatmentFinance', 'Price, discount, and costs must be valid non-negative amounts.'));
      }
      if (draft.discountEnabled && draft.discountMode === 'percent' && discountValue > 100) {
        throw new Error(tt('errors.invalidDiscountPercent', 'Percentage discount cannot exceed 100.'));
      }
      const grossPrice = price * Number(treatment.quantity || 1);
      const discountAmount = draft.discountEnabled
        ? draft.discountMode === 'percent'
          ? Number((grossPrice * discountValue / 100).toFixed(2))
          : discountValue
        : 0;
      if (discountAmount - grossPrice > 0.005) {
        throw new Error(tt('errors.discountExceedsPrice', 'Discount cannot exceed treatment price.'));
      }

      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/treatments/${treatment.treatmentId}`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          treatmentId: treatment.treatmentId,
          treatmentCategory: treatment.treatmentCategory || null,
          treatmentName: treatment.treatmentName,
          quantity: treatment.quantity || 1,
          price,
          discount: discountAmount,
          doctorId: treatment.doctorId ?? null,
          executionStatus: treatment.executionStatus,
          isIncludedFree: treatment.isIncludedFree,
          notes: treatment.notes || null,
          discountMode: draft.discountEnabled ? draft.discountMode : 'amount',
          discountValue,
          invoice: draft.invoice,
          specialMaterialsCost,
          laboratoryId: draft.laboratoryId ? Number(draft.laboratoryId) : null,
          laboratoryCost,
          mechanicName: draft.mechanicName.trim() || null,
          treatedTeeth: treatment.treatedTeeth || null
        })
      });
      if (!response.ok) throw new Error(await response.text() || tt('errors.saveTreatmentFinanceFailed', 'Failed to save treatment charges.'));
    });
  };

  const chargeRoute = (charge: CalendarDetailCharge) => {
    if (charge.treatmentId) return `treatment/${charge.treatmentId}`;
    if (charge.accountEntryId) return `account/${charge.accountEntryId}`;
    return null;
  };

  const beginChargeEdit = (charge: CalendarDetailCharge) => {
    setEditingChargeId(charge.id);
    setChargeEditDraft({ concept: charge.concept, amount: String(Number(charge.charge || 0)) });
  };

  const submitChargeEdit = (event: React.FormEvent, charge: CalendarDetailCharge) => {
    event.preventDefault();
    const route = chargeRoute(charge);
    if (!route) return;
    void run(async () => {
      const amount = Number(chargeEditDraft.amount);
      const applied = Number(charge.paid || 0) + Number(charge.reservationCreditApplied || 0) + Number(charge.reservationPendingOffset || 0);
      if (!Number.isFinite(amount) || amount < applied - 0.005) {
        throw new Error(tt('errors.chargeBelowApplied', 'Charge cannot be less than payments and credits already applied.'));
      }
      if (!chargeEditDraft.concept.trim()) {
        throw new Error(tt('errors.chargeConceptRequired', 'Charge concept is required.'));
      }
      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/charges/${route}`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ concept: chargeEditDraft.concept.trim(), amount })
      });
      if (!response.ok) throw new Error(await response.text() || tt('errors.saveChargeFailed', 'Failed to save charge.'));
      setEditingChargeId(null);
    });
  };

  const removeCharge = (charge: CalendarDetailCharge) => {
    const route = chargeRoute(charge);
    if (!route) return;
    if (!window.confirm(tt('chargeActions.removeConfirm', `Remove charge "${charge.concept}"?`))) return;
    void run(async () => {
      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/charges/${route}`), {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!response.ok) throw new Error(await response.text() || tt('errors.removeChargeFailed', 'Failed to remove charge.'));
      setEditingChargeId(null);
    });
  };

  const submitPayment = (event: React.FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const amount = Number(payment.amount);
      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/payments/quick`), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          amount,
          method: payment.method,
          notes: payment.notes || null
        })
      });
      if (!response.ok) throw new Error(await response.text() || tt('errors.collectPaymentFailed', 'Failed to collect payment'));
      setPayment({ amount: '', method: 'Cash', notes: '' });
      setPaymentOpen(false);
    });
  };

  const checkoutAllocations = (amount: number) => {
    let remaining = amount;
    const allocations: Array<{ treatmentId: number; amount: number }> = [];
    detail.treatments
      .filter(item => Number(item.due || 0) > 0.005)
      .forEach(item => {
        if (remaining <= 0.005) return;
        const applied = Math.min(Number(item.due || 0), remaining);
        if (applied > 0.005) allocations.push({ treatmentId: item.treatmentId, amount: Number(applied.toFixed(2)) });
        remaining = Number((remaining - applied).toFixed(2));
      });
    return remaining > 0.005 ? null : allocations;
  };

  const patientRegistrationPayload = () => ({
    hc: patientDraft.hc.trim() || null,
    firstName: patientDraft.firstName.trim(),
    secondName: patientDraft.secondName.trim() || null,
    firstLastName: patientDraft.firstLastName.trim(),
    secondLastName: patientDraft.secondLastName.trim() || null,
    presentedName: patientDraft.presentedName.trim() || null,
    phoneNumber: patientDraft.phoneNumber.trim() || null,
    email: patientDraft.email.trim() || null,
    dateOfBirth: patientDraft.dateOfBirth || null,
    address: patientDraft.address.trim() || null,
    referralSource: patientDraft.referralSource.trim() || null,
    nextContact: patientDraft.nextContact || null,
    notes: patientDraft.notes.trim() || null,
    isForeignPatient: patientDraft.isForeignPatient
  });

  const registerPatientNow = () => {
    void run(async () => {
      if (!patientDraft.firstName.trim() || !patientDraft.firstLastName.trim()) {
        throw new Error(tt('errors.patientNameRequired', 'First name and first surname are required.'));
      }
      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/patient-registration`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(patientRegistrationPayload())
      });
      if (!response.ok) throw new Error(await response.text() || tt('errors.patientRegistrationFailed', 'Failed to register patient.'));
      clearPatientsCache();
      setNotice(tt('patientCompletion.saved', 'Patient registered successfully.'));
    });
  };

  const submitCheckout = (event: React.FormEvent) => {
    event.preventDefault();
    void run(async () => {
      if (!canComplete) throw new Error(tt('errors.checkoutUnavailable', 'Appointment is not ready for checkout.'));
      if (needsPatientCompletion && (!patientDraft.firstName.trim() || !patientDraft.firstLastName.trim())) {
        setActiveTab('patient');
        throw new Error(tt('errors.patientNameRequired', 'First name and first surname are required.'));
      }
      const reservationAmount = reservationOpen ? Number(reservationPayment.amount) : 0;
      if (!Number.isFinite(reservationAmount) || reservationAmount < 0 || reservationAmount - Number(reservationFee?.balance || 0) > 0.005) {
        throw new Error(tt('errors.invalidReservationPayment', 'Enter a reservation payment between zero and pending reservation balance.'));
      }
      const amount = payment.amount.trim() ? Number(payment.amount) : 0;
      if (!Number.isFinite(amount) || amount < 0 || amount - currentDue > 0.005) {
        throw new Error(tt('errors.invalidCheckoutAmount', 'Enter an amount between zero and remaining appointment balance.'));
      }
      if ((amount > 0.005 || reservationAmount > 0.005) && !canEditPayments) throw new Error(tt('errors.paymentPermission', 'Payment permission is required.'));
      const allocations = checkoutAllocations(amount);
      if (allocations === null) throw new Error(tt('errors.paymentAllocation', 'Payment exceeds allocatable treatment balance.'));

      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/checkout`), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          amountReceived: amount,
          paymentMethod: amount > 0.005 ? payment.method : '',
          paymentDate: new Date().toISOString(),
          allocations,
          notes: payment.notes || null,
          reservationFeeCreditAllocations: [],
          patientDetails: needsPatientCompletion ? patientRegistrationPayload() : null,
          reservationPayment: reservationAmount > 0.005 ? {
            amount: reservationAmount,
            date: new Date().toISOString(),
            method: reservationPayment.method,
            notes: reservationPayment.notes || null
          } : null
        })
      });
      if (!response.ok) throw new Error(await response.text() || tt('errors.checkoutFailed', 'Failed to complete appointment.'));
      setPayment({ amount: '', method: 'Cash', notes: '' });
      setReservationPayment({ amount: '', method: 'Cash', notes: '' });
      setReservationOpen(false);
      clearPatientsCache();
      setForm({ ...form, status: 'completed' });
      onStatusChanged?.(appointment.id, 'completed');
    });
  };

  const submitReservationPayment = (event: React.FormEvent) => {
    event.preventDefault();
    if (!onReservationFeePayment) return;
    void run(async () => {
      await onReservationFeePayment({
        amount: Number(reservationPayment.amount),
        method: reservationPayment.method,
        date: new Date().toISOString(),
        notes: reservationPayment.notes || undefined
      });
      setReservationPayment({ amount: '', method: 'Cash', notes: '' });
      setReservationOpen(false);
    });
  };

  const submitReservationFee = (event: React.FormEvent) => {
    event.preventDefault();
    if (!onReservationFeeUpdate) return;
    void run(async () => {
      const amount = Number(reservationFeeAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error(tt('errors.invalidReservationFee', 'Enter a reservation fee greater than zero.'));
      }
      await onReservationFeeUpdate(amount);
      setReservationFeeEditorOpen(false);
    });
  };

  const removeReservationFee = () => {
    if (!onReservationFeeRemove) return;
    if (!window.confirm(tt('reservationManagement.removeConfirm', 'Remove this reservation fee?'))) return;
    void run(async () => {
      await onReservationFeeRemove();
      setReservationFeeEditorOpen(false);
    });
  };

  const removeAppointmentPayment = (paymentId: number) => {
    if (!window.confirm(tt('deletePaymentConfirm', 'Delete this appointment payment?'))) return;
    void run(async () => {
      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${appointment.id}/payments/${paymentId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!response.ok) throw new Error(await response.text() || tt('errors.deletePaymentFailed', 'Failed to delete payment'));
    });
  };

  const input: React.CSSProperties = {
    padding: '10px 12px', border: `1px solid ${tokens.app.inputBorder}`, borderRadius: 6,
    background: tokens.app.input, color: tokens.app.text, colorScheme: tokens.isLight ? 'light' : 'dark', minWidth: 0
  };
  const label: React.CSSProperties = { display: 'grid', gap: 6, color: tokens.app.muted, fontSize: 13, fontWeight: 700 };
  const button: React.CSSProperties = { padding: '10px 14px', border: 0, borderRadius: 6, background: tokens.app.primary, color: tokens.app.primaryText, cursor: 'pointer', fontWeight: 700 };
  const secondary: React.CSSProperties = { ...button, background: tokens.app.surfaceAlt, color: tokens.app.text, border: `1px solid ${tokens.app.border}` };
  const card: React.CSSProperties = { padding: 14, border: `1px solid ${tokens.app.border}`, borderRadius: 8, background: tokens.app.surfaceAlt };
  const danger: React.CSSProperties = { ...secondary, borderColor: '#ef4444', color: '#ef4444' };
  const canMutateCharges = canEditPayments || canEditAppointments;
  const patientName = detail.patient?.name || appointment.patient?.name || appointment.prospectName || tt('patient', 'Patient');
  const profilePicturePath = detail.patient?.profilePicturePath || appointment.patient?.profilePicturePath;
  const profilePictureUrl = getProfilePictureUrl(profilePicturePath);
  const patientInitials = patientName.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || '?';

  const renderEditableCharge = (charge: CalendarDetailCharge) => {
    const applied = Number(charge.paid || 0) + Number(charge.reservationCreditApplied || 0) + Number(charge.reservationPendingOffset || 0);
    const isEditing = editingChargeId === charge.id;
    return <div key={charge.id} style={{ padding: 10, border: `1px solid ${tokens.app.border}`, borderRadius: 6, background: tokens.app.surface, display: 'grid', gap: 8 }}>
      {isEditing ? <form onSubmit={event => submitChargeEdit(event, charge)} style={{ display: 'grid', gap: 10 }}>
        <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(130px, .45fr)', gap: 10 }}>
          <label style={label}>{tt('chargeActions.concept', 'Concept')}
            <input style={input} value={chargeEditDraft.concept} onChange={event => setChargeEditDraft(current => ({ ...current, concept: event.target.value }))} required />
          </label>
          <label style={label}>{tt('chargeActions.amount', 'Charge amount (Bs)')}
            <input style={input} type="number" min={applied} step="0.01" value={chargeEditDraft.amount} onChange={event => setChargeEditDraft(current => ({ ...current, amount: event.target.value }))} required />
          </label>
        </div>
        <div className="responsive-row" style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
          <button type="button" style={secondary} onClick={() => setEditingChargeId(null)}>{tt('actions.cancel', 'Cancel')}</button>
          <button type="submit" style={button} disabled={busy}>{tt('chargeActions.save', 'Save charge')}</button>
        </div>
      </form> : <>
        <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <strong>{charge.concept}</strong>
          <span style={{ color: tokens.app.muted, fontSize: 13 }}>{formatDate(charge.dueDate)}</span>
        </div>
        <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8 }}>
          <div><small style={{ color: tokens.app.muted }}>{tt('previousDebtDetails.originalCharge', 'Original charge')}</small><div>{money(charge.charge)}</div></div>
          <div><small style={{ color: tokens.app.muted }}>{tt('previousDebtDetails.paid', 'Paid')}</small><div>{money(charge.paid)}</div></div>
          <div><small style={{ color: tokens.app.muted }}>{tt('previousDebtDetails.credit', 'Reservation credit')}</small><div>{money(charge.reservationCreditApplied)}</div></div>
          <div><small style={{ color: tokens.app.muted }}>{tt('previousDebtDetails.pending', 'Pending')}</small><div><strong>{money(charge.pending)}</strong></div></div>
        </div>
        {canMutateCharges && <div className="responsive-row" style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
          <button type="button" style={secondary} disabled={busy} onClick={() => beginChargeEdit(charge)}>{tt('chargeActions.edit', 'Edit charge')}</button>
          <button
            type="button"
            style={danger}
            disabled={busy || applied > 0.005}
            title={applied > 0.005 ? tt('chargeActions.removeBlocked', 'Charges with payments or reservation credit cannot be removed.') : undefined}
            onClick={() => removeCharge(charge)}
          >{tt('chargeActions.remove', 'Remove charge')}</button>
        </div>}
      </>}
    </div>;
  };

  if (!open) return null;

  return (
    <ModalBackdrop className="calendar-modal-overlay" onDismiss={onClose}>
      <div data-testid="appointment-operational-modal" className="calendar-modal-shell" style={{ background: tokens.app.surface, color: tokens.app.text, border: `1px solid ${tokens.app.border}`, maxWidth: 900 }} onClick={event => event.stopPropagation()}>
        <div className="calendar-modal-header responsive-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
          <div className="responsive-row" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', position: 'relative', flex: '0 0 52px', display: 'grid', placeItems: 'center', background: tokens.app.activeBg, color: tokens.app.activeText, border: `1px solid ${tokens.app.border}`, fontWeight: 800 }} aria-label={tt('patientPhoto', 'Patient photo')}>
              <span>{patientInitials}</span>
              {profilePictureUrl && <img src={profilePictureUrl} alt={`${patientName} ${tt('patientPhoto', 'profile picture')}`} onError={event => { event.currentTarget.style.display = 'none'; }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
            </div>
            <div>
              <h2 style={{ margin: 0 }}>{patientName}</h2>
              <div style={{ color: tokens.app.muted, marginTop: 4 }}>{detail.appointment.appointmentType}</div>
            </div>
          </div>
          <div className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isNoShow && <strong style={{ padding: '6px 10px', borderRadius: 999, background: '#7f1d1d', color: '#fecaca' }}>{tt('status.noshow', 'No-show')}</strong>}
            {canToggleConfirmation && <button
              type="button"
              style={isConfirmed
                ? { ...secondary, borderColor: '#22c55e', color: '#22c55e' }
                : { ...button, background: '#16a34a', color: '#fff' }}
              disabled={busy}
              title={confirmationRequiresReservation ? tt('confirmation.requiresReservation', 'Pay the reservation fee in full before confirming this appointment.') : undefined}
              onClick={toggleConfirmation}
            >
              {isConfirmed
                ? tt('actions.undoConfirmation', 'Undo confirmation')
                : tt('actions.confirmed', 'Confirm appointment')}
            </button>}
            <button type="button" style={secondary} onClick={onClose} aria-label={tt('actions.close', 'Close')}>x</button>
          </div>
        </div>

        <div className="calendar-modal-body" style={{ display: 'grid', gap: 14 }}>
          {error && <div role="alert" style={{ padding: 10, borderRadius: 6, background: '#7f1d1d', color: '#fecaca' }}>{error}</div>}
          {notice && <div role="status" style={{ padding: 10, borderRadius: 6, background: '#14532d', color: '#dcfce7' }}>{notice}</div>}
          {confirmationRequiresReservation && <section className="responsive-row" style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderColor: '#f59e0b' }}>
            <div>
              <strong>{tt('confirmation.pendingReservation', 'Reservation payment required')}</strong>
              <div style={{ color: tokens.app.muted, fontSize: 13, marginTop: 3 }}>{tt('confirmation.requiresReservation', 'Pay the reservation fee in full before confirming this appointment.')}</div>
            </div>
            {canEditPayments && <button type="button" style={button} onClick={() => setActiveTab('payment')}>{tt('confirmation.recordReservation', 'Record reservation payment')}</button>}
          </section>}

          <nav className="responsive-row" aria-label={tt('tabs.navigation', 'Appointment sections')} style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${tokens.app.border}`, paddingBottom: 8 }}>
            {([
              ['appointment', tt('tabs.appointment', 'Appointment')],
              ...(needsPatientCompletion ? [['patient', tt('tabs.finishPatient', 'Finish patient')]] : []),
              ['payment', tt('tabs.payment', 'Payment')]
            ] as Array<[OperationalTab, string]>).map(([id, text]) => <button
              key={id}
              type="button"
              aria-current={activeTab === id ? 'page' : undefined}
              onClick={() => setActiveTab(id)}
              style={activeTab === id ? button : secondary}
            >{text}</button>)}
          </nav>

          {needsPatientCompletion && activeTab !== 'patient' && <section className="responsive-row" style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderColor: tokens.app.primary }}>
            <div>
              <strong>{tt('patientCompletion.pendingBanner', 'Patient registration incomplete · HC pending')}</strong>
              <div style={{ color: tokens.app.muted, fontSize: 13, marginTop: 3 }}>{tt('patientCompletion.pendingHelp', 'Finish patient details before completing appointment. HC is assigned during completion.')}</div>
            </div>
            <button type="button" style={button} onClick={() => setActiveTab('patient')}>{tt('patientCompletion.open', 'Finish patient registration')}</button>
          </section>}

          {activeTab === 'payment' && <section className="responsive-grid" style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 12 }}>
            <div><small style={{ color: tokens.app.muted }}>{tt('doctor', 'Doctor')}</small><div>{detail.doctor?.name || '-'}</div></div>
            <div><small style={{ color: tokens.app.muted }}>{tt('room', 'Room')}</small><div>{detail.appointment.room || '-'}</div></div>
            <div><small style={{ color: tokens.app.muted }}>{tt('treatmentPrice', 'Treatment price')}</small><div>{money(treatmentPrice)}</div></div>
            <div><small style={{ color: tokens.app.muted }}>{tt('reservationApplied', 'Reservation applied')}</small><div>{reservationAppliedToAppointment > 0.005 ? `-${money(reservationAppliedToAppointment)}` : money(0)}</div></div>
            <div><small style={{ color: tokens.app.muted }}>{tt('consultationRemaining', 'Consultation remaining')}</small><div style={{ color: currentDue > 0.005 ? balanceColor : undefined }}>{money(currentDue)}</div></div>
            <div><small style={{ color: tokens.app.muted }}>{tt('reservationPending', 'Reservation pending')}</small><div style={{ color: Number(reservationFee?.balance || 0) > 0.005 ? balanceColor : undefined }}>{money(reservationFee?.balance || 0)}</div></div>
            <div><small style={{ color: tokens.app.muted }}>{tt('amountDue', 'Total due')}</small><div style={{ color: Number(detail.financial.totalDue || 0) > 0.005 ? balanceColor : undefined }}>{money(detail.financial.totalDue)}</div></div>
            <div><small style={{ color: tokens.app.muted }}>{tt('oldDebt', 'Old debt')}</small><div>{money(detail.financial.previousDebt)}</div></div>
          </section>}

          {activeTab === 'payment' && <section style={{ ...card, display: 'grid', gap: 12 }}>
            <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <strong>{tt('reservationManagement.title', 'Reservation fee')}</strong>
                <div style={{ color: tokens.app.muted, fontSize: 13, marginTop: 3 }}>
                  {reservationFee
                    ? tt('reservationManagement.appointmentScoped', 'Tracked for this appointment.')
                    : tt('reservationManagement.none', 'No reservation fee recorded for this appointment.')}
                </div>
              </div>
              {canEditPayments && patientId && <div className="responsive-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {onReservationFeeUpdate && <button type="button" style={secondary} onClick={() => {
                  setReservationFeeAmount(String(reservationFee?.amount ?? 50));
                  setReservationFeeEditorOpen(value => !value);
                }}>{reservationFee ? tt('reservationManagement.edit', 'Edit reservation fee') : tt('reservationManagement.add', 'Add reservation fee')}</button>}
                {reservationFee && onReservationFeeRemove && <button type="button" style={{ ...secondary, color: '#dc2626' }} disabled={busy} onClick={removeReservationFee}>{tt('reservationManagement.remove', 'Remove reservation fee')}</button>}
              </div>}
            </div>
            {reservationFee && <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
              <div><small style={{ color: tokens.app.muted }}>{tt('reservationManagement.total', 'Total')}</small><div><strong>{money(reservationFee.amount)}</strong></div></div>
              <div><small style={{ color: tokens.app.muted }}>{tt('reservationManagement.paid', 'Paid')}</small><div>{money(reservationFee.paidAmount)}</div></div>
              <div><small style={{ color: tokens.app.muted }}>{tt('reservationManagement.pending', 'Pending')}</small><div style={{ color: reservationFee.balance > 0.005 ? balanceColor : undefined }}>{money(reservationFee.balance)}</div></div>
              <div><small style={{ color: tokens.app.muted }}>{tt('reservationManagement.status', 'Status')}</small><div>{tt(`reservationManagement.statuses.${reservationFee.status}`, reservationFee.status)}</div></div>
            </div>}
            {!patientId && <div style={{ color: tokens.app.muted, fontSize: 13 }}>{tt('reservationManagement.patientRequired', 'Register a patient before adding a reservation fee.')}</div>}
            {reservationFeeEditorOpen && <form className="responsive-row" onSubmit={submitReservationFee} style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
              <label style={{ ...label, flex: '1 1 190px' }}>{tt('reservationManagement.amount', 'Reservation fee amount (Bs)')}
                <input style={input} type="number" min="0.01" step="0.01" value={reservationFeeAmount} onChange={event => setReservationFeeAmount(event.target.value)} required />
              </label>
              <button type="button" style={secondary} onClick={() => setReservationFeeEditorOpen(false)}>{tt('actions.cancel', 'Cancel')}</button>
              <button type="submit" style={button} disabled={busy}>{tt('actions.save', 'Save')}</button>
            </form>}
          </section>}

          {activeTab === 'payment' && activeTreatmentCharges.length > 0 && <section style={{ ...card, display: 'grid', gap: 12 }}>
            <div>
              <strong>{tt('treatmentFinance.title', 'Treatment charges')}</strong>
              <div style={{ color: tokens.app.muted, fontSize: 13, marginTop: 3 }}>{tt('treatmentFinance.help', 'Edit patient price and record invoice, materials, and mechanic costs before payment.')}</div>
            </div>
            {activeTreatmentCharges.map(treatment => {
              const draft = treatmentFinanceDrafts[treatment.treatmentId];
              if (!draft) return null;
              const price = Number(draft.price || 0);
              const discountValue = draft.discountEnabled ? Number(draft.discountValue || 0) : 0;
              const grossPrice = price * Number(treatment.quantity || 1);
              const discountAmount = draft.discountEnabled && draft.discountMode === 'percent'
                ? grossPrice * discountValue / 100
                : discountValue;
              const finalPrice = Math.max(0, grossPrice - discountAmount);
              const advancedOpen = advancedTreatmentIds.has(treatment.treatmentId);
              const selectedMechanicValue = draft.laboratoryId
                ? draft.laboratoryId
                : draft.mechanicName
                  ? `legacy:${draft.mechanicName}`
                  : '';
              const selectedLaboratoryExists = draft.laboratoryId
                ? laboratories.some(laboratory => String(laboratory.id) === draft.laboratoryId)
                : false;
              const treatmentCharge = detail.financial.charges.find(charge => charge.treatmentId === treatment.treatmentId) || {
                id: `treatment:${treatment.treatmentId}`,
                concept: treatment.treatmentName,
                dueDate: detail.appointment.startTime || new Date().toISOString(),
                charge: finalPrice,
                paid: Number(treatment.paid || 0),
                reservationCreditApplied: Number(treatment.reservationCreditApplied || 0),
                reservationPendingOffset: Number(treatment.reservationPendingOffset || 0),
                pending: Number(treatment.due || finalPrice),
                treatmentId: treatment.treatmentId,
                isCurrentAppointment: true
              };
              const treatmentApplied = Number(treatmentCharge.paid || 0) + Number(treatmentCharge.reservationCreditApplied || 0) + Number(treatmentCharge.reservationPendingOffset || 0);
              return <form key={treatment.treatmentId} onSubmit={event => submitTreatmentFinance(event, treatment)} style={{ padding: 12, border: `1px solid ${tokens.app.border}`, borderRadius: 7, background: tokens.app.surface, display: 'grid', gap: 12 }}>
                <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong>{treatment.treatmentName}</strong>
                  <span>{tt('finalPrice', 'Final price')}: <strong>{money(finalPrice)}</strong></span>
                </div>
                <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(170px, 1fr) minmax(170px, 1fr)', gap: 10 }}>
                  <label style={label}>{tt('treatmentFinance.basePrice', 'Base price (Bs)')}
                    <input style={input} type="number" min="0" step="0.01" value={draft.price} disabled={!canEditAppointments || busy} onChange={event => updateTreatmentFinanceDraft(treatment.treatmentId, { price: event.target.value })} />
                  </label>
                  <label className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 8, color: tokens.app.text, fontSize: 13, fontWeight: 700, alignSelf: 'end', minHeight: 40 }}>
                    <input type="checkbox" checked={draft.invoice} disabled={!canEditAppointments || busy} onChange={event => updateTreatmentFinanceDraft(treatment.treatmentId, { invoice: event.target.checked })} />
                    {tt('treatmentFinance.invoice', 'Invoice')}
                  </label>
                </div>
                <button
                  type="button"
                  aria-expanded={advancedOpen}
                  style={{ ...secondary, justifySelf: 'start' }}
                  onClick={() => setAdvancedTreatmentIds(current => {
                    const next = new Set(current);
                    if (next.has(treatment.treatmentId)) next.delete(treatment.treatmentId);
                    else next.add(treatment.treatmentId);
                    return next;
                  })}
                >{tt('treatmentFinance.advancedOptions', 'Advanced options')}</button>
                {advancedOpen && <>
                  <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(170px, 1fr) minmax(170px, 1fr)', gap: 10 }}>
                    <label style={label}>{tt('treatmentFinance.mechanic', 'Mechanic')}
                      <select style={input} value={selectedMechanicValue} disabled={!canEditAppointments || busy} onChange={event => {
                        const selected = laboratories.find(laboratory => String(laboratory.id) === event.target.value);
                        updateTreatmentFinanceDraft(treatment.treatmentId, {
                          laboratoryId: selected ? String(selected.id) : '',
                          mechanicName: selected?.name || ''
                        });
                      }}>
                        <option value="">{tt('treatmentFinance.noMechanic', 'No mechanic')}</option>
                        {draft.mechanicName && !draft.laboratoryId && <option value={`legacy:${draft.mechanicName}`}>{draft.mechanicName}</option>}
                        {draft.laboratoryId && !selectedLaboratoryExists && <option value={draft.laboratoryId}>{draft.mechanicName || draft.laboratoryId}</option>}
                        {laboratories.map(laboratory => <option key={laboratory.id} value={laboratory.id}>{laboratory.name}</option>)}
                      </select>
                    </label>
                    <label style={label}>{tt('treatmentFinance.mechanicCost', 'Mechanic cost (Bs)')}
                      <input style={input} type="number" min="0" step="0.01" value={draft.laboratoryCost} disabled={!canEditAppointments || busy} onChange={event => updateTreatmentFinanceDraft(treatment.treatmentId, { laboratoryCost: event.target.value })} />
                    </label>
                  </div>
                  <label style={label}>{tt('treatmentFinance.materialsCost', 'Special materials (Bs)')}
                    <input style={input} type="number" min="0" step="0.01" value={draft.specialMaterialsCost} disabled={!canEditAppointments || busy} onChange={event => updateTreatmentFinanceDraft(treatment.treatmentId, { specialMaterialsCost: event.target.value })} />
                  </label>
                  <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(170px, 1fr) minmax(170px, 1fr)', gap: 10 }}>
                    <label style={label}>{tt('discountType', 'Discount type')}
                      <select style={input} value={draft.discountEnabled ? draft.discountMode : 'none'} disabled={!canEditAppointments || busy} onChange={event => {
                        const mode = event.target.value;
                        updateTreatmentFinanceDraft(treatment.treatmentId, {
                          discountEnabled: mode !== 'none',
                          discountMode: mode === 'percent' ? 'percent' : 'amount',
                          discountValue: mode === 'none' ? '' : draft.discountValue
                        });
                      }}>
                        <option value="none">{tt('treatmentFinance.noDiscount', 'No discount')}</option>
                        <option value="amount">{tt('treatmentFinance.fixedDiscount', 'Amount')}</option>
                        <option value="percent">{tt('treatmentFinance.percentDiscount', 'Percent')}</option>
                      </select>
                    </label>
                    <label style={label}>{draft.discountMode === 'percent' ? tt('treatmentFinance.discountPercentValue', 'Percent (%)') : tt('treatmentFinance.discountAmountValue', 'Amount (Bs)')}
                      <input style={input} type="number" min="0" max={draft.discountMode === 'percent' ? 100 : grossPrice} step="0.01" value={draft.discountValue} disabled={!canEditAppointments || busy || !draft.discountEnabled} onChange={event => updateTreatmentFinanceDraft(treatment.treatmentId, { discountValue: event.target.value })} />
                    </label>
                  </div>
                  <div style={{ color: tokens.app.muted, fontSize: 12 }}>{tt('treatmentFinance.costHelp', 'Materials and mechanic costs are internal costs; they do not increase patient balance.')}</div>
                </>}
                {canMutateCharges && <div className="responsive-row" style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
                  {canEditAppointments && <button type="submit" style={button} disabled={busy}>{tt('treatmentFinance.save', 'Save treatment charges')}</button>}
                  <button
                    type="button"
                    style={danger}
                    disabled={busy || treatmentApplied > 0.005}
                    title={treatmentApplied > 0.005 ? tt('chargeActions.removeBlocked', 'Charges with payments or reservation credit cannot be removed.') : undefined}
                    onClick={() => removeCharge(treatmentCharge)}
                  >{tt('chargeActions.remove', 'Remove charge')}</button>
                </div>}
              </form>;
            })}
          </section>}

          {activeTab === 'payment' && currentAccountCharges.length > 0 && <section style={{ ...card, display: 'grid', gap: 10 }}>
            <div>
              <strong>{tt('chargeActions.additionalCharges', 'Additional charges')}</strong>
              <div style={{ color: tokens.app.muted, fontSize: 13, marginTop: 3 }}>{tt('chargeActions.additionalChargesHelp', 'Charges added directly to this appointment.')}</div>
            </div>
            {currentAccountCharges.map(renderEditableCharge)}
          </section>}

          {activeTab === 'payment' && Number(detail.financial.previousDebt || 0) > 0.005 && <section style={{ ...card, display: 'grid', gap: 10 }}>
            <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <strong>{tt('previousDebtDetails.title', 'Previous debt details')}</strong>
                <div style={{ color: tokens.app.muted, fontSize: 13, marginTop: 3 }}>{tt('previousDebtDetails.help', 'Outstanding charges from before this appointment.')}</div>
              </div>
              <strong>{tt('previousDebtDetails.total', 'Total')}: {money(detail.financial.previousDebt)}</strong>
            </div>
            {previousDebtCharges.length > 0 ? <div style={{ display: 'grid', gap: 8 }}>
              {previousDebtCharges.map(renderEditableCharge)}
            </div> : <div style={{ color: tokens.app.muted, fontSize: 13 }}>{tt('previousDebtDetails.unavailable', 'Debt total is available, but charge-level details are unavailable.')}</div>}
          </section>}

          {activeTab === 'appointment' && <form onSubmit={event => { event.preventDefault(); void saveAppointment(); }} style={{ ...card, display: 'grid', gap: 12 }}>
            <strong>{tt('summary.appointment', 'Appointment')}</strong>
            <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
              <label style={label}>{tt('doctor', 'Doctor')}
                <select style={input} value={form.doctorId} onChange={event => setForm({ ...form, doctorId: event.target.value })}>
                  <option value="">{tt('actions.changeDoctor', 'Select doctor')}</option>
                  {doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}
                </select>
              </label>
              <label style={label}>{tt('date', 'Date')}<BolivianDateInput style={input} value={form.date} onChange={date => setForm({ ...form, date })} required /></label>
              {!form.isAllDay && <label style={label}>{tt('time', 'Start time')}<BolivianTimeInput style={input} value={form.startTime} onChange={startTime => setForm({ ...form, startTime })} required /></label>}
              {!form.isAllDay && <label style={label}>{tt('duration', 'End time')}<BolivianTimeInput style={input} value={form.endTime} onChange={endTime => setForm({ ...form, endTime })} required /></label>}
              <label style={label}>{tt('room', 'Room')}
                <select style={input} value={form.room || ''} onChange={event => setForm({ ...form, room: event.target.value })}>
                  <option value="">-</option>{[1, 2, 3, 4, 5].map(room => <option key={room} value={room}>{room}</option>)}
                </select>
              </label>
            </div>
            <section style={{ display: 'grid', gap: 10 }}>
              <label style={label}>{tt('appointmentTreatments.add', 'Add treatment')}
                <select style={input} value="" onChange={event => addTreatment(event.target.value)}>
                  <option value="">{tt('appointmentTreatments.select', 'Select treatment')}</option>
                  <option value="consultation">{t('calendar.modal.defaultConsultationReason', 'Consultation')}</option>
                  {detail.treatments.length > 0 && <optgroup label={tt('appointmentTreatments.linked', 'Linked treatments')}>
                    {detail.treatments.map(item => <option key={item.treatmentId} value={`treatment:${item.treatmentId}`} disabled={selectedTreatmentIds.includes(String(item.treatmentId))}>{item.treatmentName}</option>)}
                  </optgroup>}
                  <optgroup label={tt('appointmentTreatments.catalog', 'Treatment catalog')}>
                    {treatmentTemplates.filter(template => !isDefaultAppointmentReasonName(template.name) && !isDefaultAppointmentReasonName(template.nameEs)).map(template => <option key={template.id} value={`template:${template.id}`} disabled={selectedTemplateSelections.some(selection => selection.treatmentTemplateId === String(template.id))}>{displayTemplateName(template)}</option>)}
                  </optgroup>
                  <option value="other">{t('calendar.modal.otherReason', 'Other')}</option>
                </select>
              </label>
              {hasSelectedTreatments && <div aria-label={tt('appointmentTreatments.selected', 'Selected treatments')} style={{ display: 'grid', gap: 8 }}>
                {selectedTreatmentIds.map(id => {
                  const treatment = detail.treatments.find(item => String(item.treatmentId) === id);
                  const name = treatment?.treatmentName || id;
                  return <div className="responsive-row" key={`treatment:${id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: 10, border: `1px solid ${tokens.app.border}`, borderRadius: 6, background: tokens.app.surface }}>
                    <strong>{name}</strong>
                    <button type="button" style={secondary} aria-label={tt('appointmentTreatments.removeNamed', `Remove ${name}`).replace('{{name}}', name)} onClick={() => removeTreatment('treatment', id)}>{tt('actions.remove', 'Remove')}</button>
                  </div>;
                })}
                {selectedTemplateSelections.map(selection => {
                  const template = treatmentTemplates.find(item => String(item.id) === selection.treatmentTemplateId);
                  const name = template ? displayTemplateName(template) : selection.treatmentTemplateId;
                  return <div className="responsive-row" key={`template:${selection.treatmentTemplateId}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: 10, border: `1px solid ${tokens.app.border}`, borderRadius: 6, background: tokens.app.surface }}>
                    <div><strong>{name}</strong><div style={{ color: tokens.app.muted, fontSize: 12 }}>{money(Number(selection.price || 0))}</div></div>
                    <button type="button" style={secondary} aria-label={tt('appointmentTreatments.removeNamed', `Remove ${name}`).replace('{{name}}', name)} onClick={() => removeTreatment('template', selection.treatmentTemplateId)}>{tt('actions.remove', 'Remove')}</button>
                  </div>;
                })}
              </div>}
              {!hasSelectedTreatments && form.treatmentTemplateId !== '__other__' && <div style={{ color: tokens.app.muted, fontSize: 13 }}>{tt('appointmentTreatments.none', 'No treatments selected.')}</div>}
            </section>
            {form.treatmentTemplateId === '__other__' && <label style={label}>{tt('type', 'Reason')}<input style={input} value={form.customReason} onChange={event => setForm({ ...form, customReason: event.target.value })} required /></label>}
            <label style={label}>{tt('notes', 'Notes')}<textarea style={{ ...input, minHeight: 70 }} value={form.notes} onChange={event => setForm({ ...form, notes: event.target.value })} /></label>
            <button type="button" style={{ ...secondary, justifySelf: 'start' }} onClick={() => setShowMore(value => !value)}>{tt('actions.moreOptions', 'More options')}</button>
            {showMore && <label style={{ ...label, maxWidth: 320 }}>{t('calendar.modal.secondDoctor', 'Second doctor')}
              <select style={input} value={form.secondDoctorId || ''} onChange={event => setForm({ ...form, secondDoctorId: event.target.value })}>
                <option value="">-</option>{doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}
              </select>
            </label>}
            <div className="responsive-row" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><button type="submit" style={button} disabled={busy}>{tt('actions.saveChanges', 'Save changes')}</button></div>
          </form>}

          {activeTab === 'patient' && needsPatientCompletion && <section style={{ ...card, display: 'grid', gap: 12 }}>
            <div>
              <strong>{tt('patientCompletion.title', 'Finish patient registration')}</strong>
              <div style={{ color: tokens.app.muted, fontSize: 13, marginTop: 4 }}>{tt('patientCompletion.hcHelp', 'Patient record and HC are saved when appointment is completed.')}</div>
            </div>
            <label style={label}>{tt('patientCompletion.hc', 'HC')}
              <input
                style={input}
                aria-label={tt('patientCompletion.hc', 'HC')}
                value={patientDraft.hc}
                onChange={event => setPatientDraft(current => ({ ...current, hc: event.target.value }))}
                placeholder={patientDraft.isForeignPatient ? '1-9' : '206'}
              />
              <span style={{ color: tokens.app.muted, fontSize: 12, fontWeight: 400 }}>{tt('patientCompletion.hcManualHelp', 'Enter HC manually, or leave blank to assign next available HC on completion.')}</span>
            </label>
            <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              <label style={label}>{tt('patientCompletion.firstName', 'First name')}<input style={input} value={patientDraft.firstName} onChange={event => setPatientDraft(current => ({ ...current, firstName: event.target.value }))} required /></label>
              <label style={label}>{tt('patientCompletion.secondName', 'Second name')}<input style={input} value={patientDraft.secondName} onChange={event => setPatientDraft(current => ({ ...current, secondName: event.target.value }))} /></label>
              <label style={label}>{tt('patientCompletion.firstLastName', 'First surname')}<input style={input} value={patientDraft.firstLastName} onChange={event => setPatientDraft(current => ({ ...current, firstLastName: event.target.value }))} required /></label>
              <label style={label}>{tt('patientCompletion.secondLastName', 'Second surname')}<input style={input} value={patientDraft.secondLastName} onChange={event => setPatientDraft(current => ({ ...current, secondLastName: event.target.value }))} /></label>
              <label style={label}>{tt('patientCompletion.presentedName', 'Display name')}<input style={input} value={patientDraft.presentedName} onChange={event => setPatientDraft(current => ({ ...current, presentedName: event.target.value }))} /></label>
              <label style={label}>{tt('patientCompletion.phone', 'Phone')}<input style={input} type="tel" value={patientDraft.phoneNumber} onChange={event => setPatientDraft(current => ({ ...current, phoneNumber: event.target.value }))} /></label>
              <label style={label}>{tt('patientCompletion.email', 'Email')}<input style={input} type="email" value={patientDraft.email} onChange={event => setPatientDraft(current => ({ ...current, email: event.target.value }))} /></label>
              <label style={label}>{tt('patientCompletion.birthDate', 'Birth date')}<BolivianDateInput style={input} value={patientDraft.dateOfBirth} onChange={dateOfBirth => setPatientDraft(current => ({ ...current, dateOfBirth }))} /></label>
              <label style={label}>{tt('patientCompletion.address', 'Address')}<input style={input} value={patientDraft.address} onChange={event => setPatientDraft(current => ({ ...current, address: event.target.value }))} /></label>
              <label style={label}>{tt('patientCompletion.referralSource', 'Referral source')}<input style={input} value={patientDraft.referralSource} onChange={event => setPatientDraft(current => ({ ...current, referralSource: event.target.value }))} /></label>
              <label style={label}>{tt('patientCompletion.nextContact', 'Next contact')}<BolivianDateInput style={input} value={patientDraft.nextContact} onChange={nextContact => setPatientDraft(current => ({ ...current, nextContact }))} /></label>
            </div>
            <label style={label}>{tt('patientCompletion.notes', 'Patient notes')}<textarea style={{ ...input, minHeight: 80 }} value={patientDraft.notes} onChange={event => setPatientDraft(current => ({ ...current, notes: event.target.value }))} /></label>
            <label className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 8, color: tokens.app.text, fontSize: 13, fontWeight: 700 }}>
              <input type="checkbox" checked={patientDraft.isForeignPatient} onChange={event => setPatientDraft(current => ({ ...current, isForeignPatient: event.target.checked }))} />
              {tt('patientCompletion.foreign', 'Foreign patient')}
            </label>
            {!canComplete && <div style={{ padding: 10, borderRadius: 6, background: tokens.app.activeBg, color: tokens.app.activeText, fontSize: 13 }}>{tt('patientCompletion.readyHelp', 'Register patient now, or save patient details together when appointment is completed later.')}</div>}
            <div className="responsive-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              {canEditAppointments && <button type="button" style={button} disabled={busy} onClick={registerPatientNow}>{busy ? tt('patientCompletion.saving', 'Registering...') : tt('patientCompletion.registerNow', 'Register patient now')}</button>}
              <button type="button" style={secondary} onClick={() => setActiveTab('payment')}>{tt('actions.continueToPayment', 'Continue to payment')}</button>
            </div>
          </section>}

          {activeTab === 'payment' && canComplete && <form onSubmit={submitCheckout} style={{ ...card, display: 'grid', gap: 12 }}>
            <div>
              <strong>{tt('checkout.title', 'Complete appointment')}</strong>
              <div style={{ color: tokens.app.muted, fontSize: 13, marginTop: 4 }}>{tt('checkout.help', 'Record reservation and appointment payments, then complete appointment.')}</div>
            </div>
            {canEditPayments && reservationFee && reservationFee.balance > 0.005 && <section style={{ padding: 12, border: `1px solid ${tokens.app.border}`, borderRadius: 7, background: tokens.app.surface, display: 'grid', gap: 10 }}>
              <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                <div><small style={{ color: tokens.app.muted }}>{tt('checkout.reservationTotal', 'Reservation total')}</small><div>{money(reservationFee.amount)}</div></div>
                <div><small style={{ color: tokens.app.muted }}>{tt('checkout.reservationPaid', 'Already paid')}</small><div>{money(reservationFee.paidAmount)}</div></div>
                <div><small style={{ color: tokens.app.muted }}>{tt('checkout.reservationPending', 'Reservation pending')}</small><div><strong style={{ color: reservationFee.balance > 0.005 ? balanceColor : undefined }}>{money(reservationFee.balance)}</strong></div></div>
              </div>
              <label className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 8, color: tokens.app.text, fontSize: 13, fontWeight: 700 }}>
                <input type="checkbox" checked={reservationOpen} onChange={event => {
                  const checked = event.target.checked;
                  setReservationOpen(checked);
                  if (checked) {
                    const reservationAmount = reservationFee.balance;
                    setReservationPayment(current => ({ ...current, amount: current.amount || reservationAmount.toFixed(2) }));
                    setPayment(current => ({ ...current, amount: current.amount || currentDue.toFixed(2) }));
                  }
                }} />
                {tt('checkout.collectReservation', 'Collect pending reservation during checkout')}
              </label>
              {reservationOpen && <div style={{ display: 'grid', gap: 10 }}>
                <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label style={label}>{tt('checkout.reservationAmount', 'Reservation payment')}<input style={input} type="number" min="0.01" max={reservationFee.balance} step="0.01" value={reservationPayment.amount} onChange={event => setReservationPayment({ ...reservationPayment, amount: event.target.value })} required /></label>
                  <label style={label}>{tt('checkout.reservationMethod', 'Reservation method')}<select style={input} value={reservationPayment.method} onChange={event => setReservationPayment({ ...reservationPayment, method: event.target.value })}>{paymentMethodOptions.map(method => <option key={method.value} value={method.value}>{method.label}</option>)}</select></label>
                </div>
                <label style={label}>{tt('checkout.reservationNotes', 'Reservation notes')}<textarea style={input} value={reservationPayment.notes} onChange={event => setReservationPayment({ ...reservationPayment, notes: event.target.value })} /></label>
              </div>}
            </section>}
            {canEditPayments && <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
              <div><small style={{ color: tokens.app.muted }}>{tt('checkout.appointmentBeforeReservation', 'Consultation remaining')}</small><div style={{ color: checkoutAppointmentBalance > 0.005 ? balanceColor : undefined }}>{money(checkoutAppointmentBalance)}</div></div>
              <div><small style={{ color: tokens.app.muted }}>{tt('checkout.appointmentAfterReservation', 'Combined balance after reservation payment')}</small><div><strong style={{ color: checkoutCombinedBalance > 0.005 ? balanceColor : undefined }}>{money(checkoutCombinedBalance)}</strong></div></div>
            </div>}
            {canEditPayments && currentDue > 0.005 && <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={label}>{tt('checkout.appointmentPayment', 'Appointment payment')}<input style={input} type="number" min="0" max={checkoutAppointmentBalance} step="0.01" value={payment.amount} onChange={event => setPayment({ ...payment, amount: event.target.value })} placeholder="0.00" /></label>
              <label style={label}>{tt('checkout.appointmentMethod', 'Appointment method')}<select style={input} value={payment.method} onChange={event => setPayment({ ...payment, method: event.target.value })}>{paymentMethodOptions.map(method => <option key={method.value} value={method.value}>{method.label}</option>)}</select></label>
            </div>}
            {canEditPayments && <label style={label}>{tt('checkout.appointmentNotes', 'Appointment payment notes')}<textarea style={input} value={payment.notes} onChange={event => setPayment({ ...payment, notes: event.target.value })} /></label>}
            {!canEditPayments && <div style={{ color: tokens.app.muted, fontSize: 13 }}>{tt('checkout.noPaymentPermission', 'Appointment will be completed without recording payment.')}</div>}
            <button type="submit" style={{ ...button, justifySelf: 'end' }} disabled={busy}>{busy ? tt('checkout.completing', 'Completing...') : tt('checkout.complete', 'Complete appointment')}</button>
          </form>}

          {activeTab === 'payment' && !canComplete && canEditPayments && currentDue > 0.005 && <section style={{ ...card, display: 'grid', gap: 10 }}>
            <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}><div><strong>{tt('actions.registerPayment', 'Register payment')}</strong><div style={{ color: currentDue > 0.005 ? balanceColor : tokens.app.muted, fontSize: 13 }}>{tt('today', 'Current appointment')}: {money(currentDue)}</div></div><button type="button" style={button} onClick={() => { setPaymentOpen(value => !value); setPayment(current => ({ ...current, amount: current.amount || currentDue.toFixed(2) })); }}>{tt('actions.registerPayment', 'Register payment')}</button></div>
            {paymentOpen && <form onSubmit={submitPayment} style={{ display: 'grid', gap: 10 }}>
              <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={label}>{tt('paymentAmount', 'Amount')}<input style={input} type="number" min="0.01" max={currentDue} step="0.01" value={payment.amount} onChange={event => setPayment({ ...payment, amount: event.target.value })} required /></label>
                <label style={label}>{tt('method', 'Method')}<select style={input} value={payment.method} onChange={event => setPayment({ ...payment, method: event.target.value })}>{paymentMethodOptions.map(method => <option key={method.value} value={method.value}>{method.label}</option>)}</select></label>
              </div>
              <label style={label}>{tt('notes', 'Notes')}<textarea style={input} value={payment.notes} onChange={event => setPayment({ ...payment, notes: event.target.value })} /></label>
              <div className="responsive-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" style={secondary} onClick={() => setPaymentOpen(false)}>{tt('actions.cancel', 'Cancel')}</button><button type="submit" style={button} disabled={busy}>{tt('actions.registerPayment', 'Register payment')}</button></div>
            </form>}
          </section>}

          {activeTab === 'payment' && canViewPayments && appointmentPayments.length > 0 && <section style={{ ...card, display: 'grid', gap: 10 }}>
            <strong>{tt('appointmentPayments', 'Payments for this appointment')}</strong>
            <div style={{ display: 'grid', gap: 8 }}>
              {appointmentPayments.map(item => <div className="responsive-grid" key={item.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, auto) minmax(90px, auto) minmax(100px, 1fr) auto', gap: 10, alignItems: 'center', padding: 10, border: `1px solid ${tokens.app.border}`, borderRadius: 6, background: tokens.app.surface }}>
                <div><small style={{ color: tokens.app.muted }}>{tt('date', 'Date')}</small><div>{formatDate(item.date)}</div></div>
                <div><small style={{ color: tokens.app.muted }}>{tt('method', 'Method')}</small><div>{item.method || '-'}</div></div>
                <div><strong>{money(item.amount)}</strong>{item.appliedTo.length > 0 && <div style={{ color: tokens.app.muted, fontSize: 12 }}>{item.appliedTo.join(', ')}</div>}{item.notes && <div style={{ color: tokens.app.muted, fontSize: 12 }}>{item.notes}</div>}</div>
                {canEditPayments && <button type="button" style={{ ...secondary, color: '#dc2626' }} disabled={busy} onClick={() => removeAppointmentPayment(item.id)}>{tt('actions.remove', 'Delete')}</button>}
              </div>)}
            </div>
          </section>}

          {activeTab === 'payment' && !canComplete && canEditPayments && reservationFee && reservationFee.balance > 0.005 && <section style={{ ...card, display: 'grid', gap: 10 }}>
            <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}><div><strong>{tt('reservationFee', 'Reservation fee')}</strong><div style={{ color: reservationFee.balance > 0.005 ? balanceColor : tokens.app.muted, fontSize: 13 }}>{tt('pending', 'Pending')}: {money(reservationFee.balance)}</div></div><button type="button" style={secondary} onClick={() => { setReservationOpen(value => !value); setReservationPayment(current => ({ ...current, amount: current.amount || reservationFee.balance.toFixed(2) })); }}>{tt('actions.recordReservationPayment', 'Record reservation payment')}</button></div>
            {reservationOpen && <form onSubmit={submitReservationPayment} style={{ display: 'grid', gap: 10 }}>
              <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={label}>{tt('paymentAmount', 'Amount')}<input style={input} type="number" min="0.01" max={reservationFee.balance} step="0.01" value={reservationPayment.amount} onChange={event => setReservationPayment({ ...reservationPayment, amount: event.target.value })} required /></label>
                <label style={label}>{tt('method', 'Method')}<select style={input} value={reservationPayment.method} onChange={event => setReservationPayment({ ...reservationPayment, method: event.target.value })}>{paymentMethodOptions.map(method => <option key={method.value} value={method.value}>{method.label}</option>)}</select></label>
              </div>
              <label style={label}>{tt('notes', 'Notes')}<textarea style={input} value={reservationPayment.notes} onChange={event => setReservationPayment({ ...reservationPayment, notes: event.target.value })} /></label>
              <div className="responsive-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" style={secondary} onClick={() => setReservationOpen(false)}>{tt('actions.cancel', 'Cancel')}</button><button type="submit" style={button} disabled={busy}>{tt('actions.save', 'Save')}</button></div>
            </form>}
          </section>}

          {activeTab === 'appointment' && <section className="responsive-row" style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {patientId && onOpenPatientProfile && <button type="button" style={secondary} onClick={() => onOpenPatientProfile(patientId)}>{tt('actions.openFullPatientAccount', 'Open patient profile / finance')}</button>}
            {linkedTreatment && onOpenTreatmentWorkspace && <button type="button" style={secondary} onClick={() => onOpenTreatmentWorkspace(linkedTreatment.treatmentId, patientId)}>{tt('actions.open', 'Open treatment workspace')}</button>}
            <button type="button" style={{ ...secondary, marginLeft: 'auto', color: '#dc2626' }} onClick={onDelete}>{tt('actions.deleteAppointment', 'Delete appointment')}</button>
            <button type="button" style={isNoShow ? { ...secondary, color: '#dc2626' } : secondary} disabled={busy} onClick={markNoShow}>{isNoShow ? tt('actions.undoNoShow', 'Undo no-show') : tt('actions.markNoShow', 'Mark no-show')}</button>
          </section>}
        </div>
      </div>
    </ModalBackdrop>
  );
};
